import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Toast } from 'primereact/toast';
import pushNotificationService from '../../services/push-notification.service';
import { getContactId } from '../../utils/current-user';

function readRoleSelected(): boolean | undefined {
  try {
    const token = localStorage.getItem('accessToken');
    const seg = token?.split('.')[1]?.replace(/-/g, '+').replace(/_/g, '/');
    if (!seg) return undefined;
    return JSON.parse(atob(seg))?.roleSelected;
  } catch {
    return undefined;
  }
}

// Module-level so it survives remounts. Role auto-select triggers a full page
// reload, and the auth state settles asynchronously (token → user → role), so a
// single component instance can't rely on firing exactly once at the right time.
const registeredContactIds = new Set<string>();

/**
 * NotificationInitializer — silently registers the FCM device token once the
 * session is fully ready (authenticated + role selected + contactId resolvable)
 * AND notification permission is already "granted".
 *
 * It polls briefly because the pieces arrive at different times, especially for
 * single-role logins (no manual role screen / reload to synchronise everything).
 * The interactive permission prompt for "default"/"denied" is handled separately
 * by PushPermissionGate — this component never prompts, so it can't spam.
 */
const NotificationInitializer = () => {
  const user = useSelector((state: any) => state.auth.user);
  const isAuthenticated = useSelector((state: any) => state.auth.isAuthenticated);
  const toast = useRef<Toast>(null);

  const showAlert = (
    message: string,
    severity: 'success' | 'info' | 'warn' | 'error' = 'info',
  ) => {
    toast.current?.show({ severity, summary: 'Notifications', detail: message, life: 5000 });
  };

  useEffect(() => {
    if (!isAuthenticated) {
      registeredContactIds.clear(); // fresh start on next login
      return;
    }
    if (!pushNotificationService.isSupported()) return;
    // Turning push off leaves permission "granted" and no stored token — exactly
    // the state this component treats as "needs registering". It re-subscribed
    // within a second of the user disabling, minting a fresh token and a fresh
    // device row, while the settings card still read "Not enabled". Only an
    // explicit re-enable clears this.
    if (pushNotificationService.isExplicitlyDisabled()) return;

    let cancelled = false;
    let tries = 0;
    const MAX_TRIES = 20; // ~20s of readiness polling

    const attempt = async () => {
      if (cancelled) return;
      tries += 1;

      const roleSelected = readRoleSelected();
      const contactId = getContactId(user);
      const ready = roleSelected !== false && !!contactId;

      if (ready && !registeredContactIds.has(contactId)) {
        // Already hold a token for this device (e.g. the permission gate just
        // registered it)? Consider it done — don't re-post and hit a 409 loop.
        if (await pushNotificationService.checkSubscriptionStatus()) {
          registeredContactIds.add(contactId);
          return;
        }
        // Only auto-register when permission is already granted; the modal gate
        // owns the interactive prompt for "default"/"denied".
        if (pushNotificationService.getPermissionStatus() === 'granted') {
          try {
            await pushNotificationService.subscribe(contactId);
            registeredContactIds.add(contactId);
            showAlert('Push notifications enabled on this device.', 'success');
            return;
          } catch (err: any) {
            console.warn('[NotificationInit] register retry:', err?.message || err);
            // fall through to retry
          }
        } else {
          return; // not granted → nothing to do here
        }
      } else if (ready && registeredContactIds.has(contactId)) {
        return; // already done this session
      }

      if (!cancelled && tries < MAX_TRIES) {
        setTimeout(attempt, 1000);
      }
    };

    const first = setTimeout(attempt, 600);
    return () => {
      cancelled = true;
      clearTimeout(first);
    };
  }, [isAuthenticated, user]);

  return <Toast ref={toast} position="top-right" className="mt-16" />;
};

export default NotificationInitializer;
